<template>
  <v-data-table
    hide-default-footer
    :headers="headers2show"
    :items="items"
    v-bind="pagingOptions"
    show-select
    v-model="selectedRows"
    @current-items="current = $event"
    @item-selected="bulkSelect"
    item-key="id"
    :sort-by="sortBy"
    :search="search"
  >
    <template v-slot:item.status="{ item }">
      <v-edit-dialog :return-value.sync="item.status" large v-if="!(item.source && item.source.name === 'GTWEB_EXT' && item.status !== 'MP Quotation') && !isSellerMpLocked(item)">
        <!-- ^ https://tracker.globetrack.ie/issues/9119 + #11287 seller MP lock -->
        <span :class="rowIsDisabled(item) ? 'grey--text' : null">
          {{ item.status }}
        </span>
        <template v-slot:input>
          <service-status-list-select
            :is-disabled="false"
            v-model="item.statusId"
            :allowed-status-names="sellerMpAllowedStatuses(item)"
            @onSelect="onItemStatusSelect(item, $event)"
          ></service-status-list-select>
        </template>
      </v-edit-dialog>
      <span v-else>
        {{ item.status }}
      </span>
    </template>
    <template v-slot:item.includeCommission="{ item }">
      <v-simple-checkbox
        v-model="item.includeCommission"
        @input="onIncludeCommissionChange(item, $event)"
      ></v-simple-checkbox>
    </template>
    <template v-slot:item.businessModel="{ item }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on">{{ item.businessModel }}</span>
        </template>
        <span>{{ item.businessModelName }}</span>
      </v-tooltip>
    </template>
    <template v-slot:item.boardBasis="{ item }">
      <v-edit-dialog :return-value.sync="item.boardBasis" large>
        {{ item.boardBasis }}
        <template v-slot:input>
          <board-basis-list-select
            v-model="item.boardBasis"
          ></board-basis-list-select>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:item.startDate="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ item.startDate | formatDate }}
      </span>
    </template>
    <template v-slot:item.isHidden="{ item }">
      <v-simple-checkbox
        v-model="item.isHidden"
        :disabled="item.isExtra && parentIsHidden(item)"
        @input="onIsHiddenChange(item, $event)"
      ></v-simple-checkbox>
    </template>
    <template v-slot:item.endDate="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ item.endDate | formatDate }}
      </span>
    </template>
    <template v-slot:item.lastUpdated="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ item.lastUpdated | formatDateTime }}
      </span>
    </template>
    <template v-slot:item.serviceType="{ item }">
      <v-icon :color="rowIsDisabled(item) ? 'grey lighten-3' : null">
        {{ supplierTypeIcon(item.serviceType) }}
      </v-icon>
    </template>
    <template v-slot:item.orderNo="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        <v-edit-dialog :return-value.sync="item.orderNo" lazy large>
          {{ item.orderNo }}
          <template v-slot:input>
            <v-text-field
              :disabled="rowIsDisabled(item)"
              v-model="item.orderNo"
              label="Edit"
              single-line
              type="number"
              @change="onOrderNoChange(item)"
            ></v-text-field>
          </template>
        </v-edit-dialog>
      </span>
    </template>
    <template v-slot:item.duration="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ duration(item) }}
      </span>
    </template>
    <template v-slot:item.supplierName="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        <supplier-label
          v-model="item.supplierName"
          :supplier-type="item.serviceType"
          :supplier-id="item.supplierId"
          :callback="showSupplierDetails(item)"
          :source="item.source"
          :year-shift="item.yearShift"
          :link-parent="item.linkParent"
        ></supplier-label>
      </span>
    </template>

    <template v-slot:item.rateTypeCode="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ item.rateTypeCode }}
      </span>
    </template>
    <template v-slot:item.qty="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ item.qty }}
      </span>
    </template>

    <template v-slot:item.serviceName="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        <v-row v-if="item.isExtra" align="center">
          <v-icon color="yellow darken-2">mdi-star</v-icon>
          {{ item.boardBasis }} ({{ item.serviceName }})
          <from-departure-icon v-if="item.fromDeparture" />
        </v-row>
        <span v-else>
          {{ item.serviceName }}
          <from-departure-icon v-if="item.fromDeparture" />
        </span>
      </span>
    </template>
    <template v-slot:item.pax="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ paxLabeler(item) }}
      </span>
    </template>
    <template v-slot:item.price="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ priceLabeler(item.price) }}
      </span>
    </template>
    <template v-slot:item.markup="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ priceLabeler(item.markup) }}
      </span>
    </template>
    <template v-slot:item.margin="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ priceLabeler(item.margin) }}
      </span>
    </template>
    <template v-slot:item.isIncluded="{ item }">
      <v-layout justify-center>
        <v-checkbox
          :disabled="
            rowIsDisabled(item) ||
            flagsEditDisabledFn(item) ||
            (item.isExtra && !parentIsIncluded(item))
          "
          @change="onIsIncludedChange(item)"
          class="pa-0 ma-0"
          v-model="item.isIncluded"
          hide-details
        ></v-checkbox>
      </v-layout>
    </template>
    <template v-slot:item.isOptional="{ item }">
      <v-layout justify-center>
        <v-checkbox
          :disabled="
            rowIsDisabled(item) ||
            flagsEditDisabledFn(item) ||
            (item.isExtra && parentIsOptional(item))
          "
          @change="onIsOptionalChange(item)"
          class="pa-0 ma-0"
          v-model="item.isOptional"
          hide-details
        ></v-checkbox>
      </v-layout>
    </template>
    <template v-slot:item.supplierCommission="{ item }">
      <span :class="rowIsDisabled(item) ? 'grey--text' : null">
        {{ item.supplierCommissionValue | priceLabeler }}
      </span>
    </template>
    <template v-slot:item.supplierComp="{ item }">
      <v-edit-dialog
        :return-value.sync="item.supplierComp"
        lazy
        large
        @save="onCompsSave"
        v-if="basicComps"
      >
        {{ item.supplierComp }}
        <template v-slot:input>
          <v-text-field
            v-model="item.supplierComp"
            label="Edit"
            single-line
            type="number"
          ></v-text-field>
        </template>
      </v-edit-dialog>
      <v-btn v-else text @click="onShowItiComps(item)">{{
        item.supplierComp
      }}</v-btn>
    </template>
    <template v-slot:item.passComp="{ item }">
      <v-edit-dialog
        :return-value.sync="item.passComp"
        lazy
        large
        @save="onCompsSave"
        v-if="basicComps"
      >
        {{ item.passComp }}
        <template v-slot:input>
          <v-text-field
            v-model="item.passComp"
            label="Edit"
            single-line
            type="number"
          ></v-text-field>
        </template>
      </v-edit-dialog>
      <v-btn v-else text @click="onShowItiComps(item)">{{
        item.passComp
      }}</v-btn>
    </template>
    <template v-slot:item.remarks.pickupTime="{ item }">
      <v-edit-dialog :return-value.sync="item.remarks.pickupTime" large>
        <v-icon left small class="mx-0">access_time</v-icon>
        {{
          item.remarks.pickupTime != "" ? item.remarks.pickupTime : "Set Time"
        }}
        <template v-slot:input>
          <v-text-field
            v-model="item.remarks.pickupTime"
            @change="onTimeChange(item)"
            label="Edit"
            type="time"
            clearable
            required
          ></v-text-field>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:item.remarks.dropoffTime="{ item }">
      <v-edit-dialog :return-value.sync="item.remarks.dropoffTime" large>
        <v-icon left small class="mx-0">access_time</v-icon>
        {{
          item.remarks.dropoffTime != "" ? item.remarks.dropoffTime : "Set Time"
        }}
        <template v-slot:input>
          <v-text-field
            v-model="item.remarks.dropoffTime"
            @change="onTimeChange(item)"
            label="Edit"
            type="time"
            clearable
            required
          ></v-text-field>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-layout class="d-flex flex-no-wrap" align-center>
        <v-btn
          small
          :disabled="rowIsDisabled(item) || item.isExtra || item.fromDeparture"
          icon
          @click="onItemEdit(item)"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small color="teal" v-on="on">mdi-pencil</v-icon>
            </template>
            <span>Modify Item</span>
          </v-tooltip>
        </v-btn>
        <v-btn icon small @click="onEditRemarks(item)">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon color="primary" small v-on="on">mdi-note-outline</v-icon>
            </template>
            <span>Remarks</span>
          </v-tooltip>
        </v-btn>
        <v-btn
          icon
          small
          :disabled="rowIsDisabled(item) || item.fromDeparture"
          @click="onEditItemPrices(item)"
          v-if="item.serviceType != 'MANDATORY_CHARGES'"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon :color="customPricingFlagColor(item)" small v-on="on">{{
                item.customPricing ? "mdi-currency-usd" : "mdi-currency-usd-off"
              }}</v-icon>
            </template>
            <span>Pricing</span>
          </v-tooltip>
        </v-btn>
        <v-avatar
          v-if="item.allocation && item.allocation != ''"
          color="indigo lighten-2"
          size="26"
          dark
          ><span class="white--text"><strong>FA</strong></span></v-avatar
        >
        <v-tooltip bottom v-if="item.hasPurchaseInvoice">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on">mdi-information-variant</v-icon>
          </template>
          <span
            >This service cannot be deleted because there is a purchase invoice
            associated with it. If you remove the purchase invoice in the
            Accounts section then you can delete this service.</span
          >
        </v-tooltip>
        <confirmation-dialog
          @onConfirm="onItemRemove"
          :item="item"
          v-else-if="itemIsDeletable(item)"
        >
          <v-btn :disabled="rowIsDisabled(item) || item.isExternal" small icon>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon small color="pink" v-on="on">mdi-delete</v-icon>
              </template>
              <span>Remove Item</span>
            </v-tooltip>
          </v-btn>
          <p slot="message">This will remove service.</p>
        </confirmation-dialog>
      </v-layout>
    </template>

    <template v-slot:footer>
      <v-speed-dial
        v-show="selectedRows.length > 0"
        open-on-hover
        absolute
        direction="right"
        v-model="modifyServicesFab"
      >
        <template v-slot:activator>
          <v-btn v-model="modifyServicesFab" color="blue darken-2" dark fab>
            <v-icon v-if="modifyServicesFab">mdi-close</v-icon>
            <v-icon v-else>mdi-menu</v-icon>
          </v-btn>
        </template>
        <v-btn
          fab
          dark
          small
          color="indigo"
          @click="onShowMoveDays"
          v-if="!selectedIncludeExternalServices"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">mdi-calendar</v-icon>
            </template>
            <span>Move Days</span>
          </v-tooltip>
        </v-btn>
        <v-btn fab dark small color="green" @click="onReorderServices">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">mdi-sort</v-icon>
            </template>
            <span>Reorder Services</span>
          </v-tooltip>
        </v-btn>
        <v-btn
          fab
          dark
          small
          color="blue"
          @click="onCustomPriceToggle"
          v-if="canEditPrices && !selectedIncludeExternalServices"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">mdi-currency-usd</v-icon>
            </template>
            <span>Custom Price on</span>
          </v-tooltip>
        </v-btn>
        <v-btn
          fab
          dark
          small
          color="orange"
          @click="onZeroPrices"
          v-if="canEditPrices"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">mdi-numeric-0</v-icon>
            </template>
            <span>Set Zero Sell Prices</span>
          </v-tooltip>
        </v-btn>
        <v-btn
          fab
          dark
          small
          color="pink"
          @click="onPriceAdjuster"
          v-if="canEditPrices && $store.getters.hasAccessTo('itineraries:adjust_prices')"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">mdi-percent</v-icon>
            </template>
            <span>Adjust Prices</span>
          </v-tooltip>
        </v-btn>
        <v-btn
          fab
          dark
          small
          color="indigo lighten-1"
          @click="onShowPaxUpdater"
          v-if="!selectedIncludeExternalServices"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">mdi-account</v-icon>
            </template>
            <span>Update Pax</span>
          </v-tooltip>
        </v-btn>
        <v-btn
          fab
          dark
          small
          color="teal accent-4"
          @click="onShowExchangeRateUpdater"
          v-if="canEditPrices"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">mdi-currency-usd</v-icon>
            </template>
            <span>Update Exchange Rate</span>
          </v-tooltip>
        </v-btn>
        <v-btn
          fab
          dark
          small
          color="red"
          @click="onRemoveSelectedServices"
          v-if="!selectedIncludeExternalServices"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">mdi-delete</v-icon>
            </template>
            <span>Remove Services</span>
          </v-tooltip>
        </v-btn>
        <v-btn
          fab
          dark
          small
          color="green"
          @click="onShowUpdateStatus"
          v-if="!selectedIncludeExternalServices && !updateStatusDisabled"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">mdi-check</v-icon>
            </template>
            <span>Update Status</span>
          </v-tooltip>
        </v-btn>
        <v-btn
          fab
          dark
          small
          color="pink"
          @click="onRefreshPrices"
          v-if="canEditPrices"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">mdi-refresh</v-icon>
            </template>
            <span>Refresh Prices</span>
          </v-tooltip>
        </v-btn>
      </v-speed-dial>
      <v-layout justify-space-around>
        <span>
          Total:
          <strong>{{ totalPrice | priceLabeler }} {{ currencyCode }}</strong>
        </span>
      </v-layout>
    </template>
  </v-data-table>
</template>

<script>
import { SupplierTypeIconMixin } from "../../../mixins/SupplierTypeIconMixin";
import ConfirmationDialog from "./ConfirmationDialog";
import { pagingOptions } from "../../../helpers/index";
import SupplierLabel from "./labels/SupplierLabel";
import TimeLabel from "./labels/TimeLabel";
import BoardBasisListSelect from "../shared/BoardBasisListSelect.vue";
import ServiceStatusListSelect from "../shared/ServiceStatusListSelect";
import FromDepartureIcon from "../shared/FromDepartureIcon.vue";
import ListBulkSelect from "../../../mixins/ListBulkSelect";
import EventBus from "../../../store/event-bus";
import PricingLogMixin from "../itineraries/builder/mixins/pricing-log";
const moment = require("moment");
const randomize = require("randomatic");
export default {
  name: "ItineraryItemsListTable",

  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    visibleColumns: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    currencyCode: {
      type: String,
      default: () => "",
    },
    disableRow: {
      type: Function,
      default: (item) => false,
    },
    flagsEditDisabledFn: {
      type: Function,
      default: (item) => false,
    },
    basicComps: {
      type: Boolean,
      default: () => true,
    },
    search: {
      type: String,
      default: "",
    },
  },
  mixins: [SupplierTypeIconMixin, ListBulkSelect, PricingLogMixin],
  components: {
    ConfirmationDialog,
    SupplierLabel,
    TimeLabel,
    BoardBasisListSelect,
    ServiceStatusListSelect,
    FromDepartureIcon,
  },
  data() {
    return {
      pagingOptions,
      debug: false,
      uid: randomize("Aa0", 32),
      modifyServicesFab: false,
    };
  },
  computed: {
    canEditPrices() {
      return this.$store.getters.hasAccessTo("itineraries:edit_prices", true);
    },
    sortBy() {
      return this.$store.getters.localSettings("itBuilderItemsSortBy");
    },
    selectedIncludeExternalServices() {
      return this.selectedRows.find((item) => item.isExternal) != null;
    },
    // #11287 part 1a: disable the Update Status tool outright when every selected
    // service is a seller MP service still at "MP Quotation" (not yet requested).
    updateStatusDisabled() {
      return (
        this.selectedRows.length > 0 &&
        this.selectedRows.every((i) => this.isSellerMpLocked(i))
      );
    },
    headers2show() {
      return [
        ...this.headers.filter(
          (h) => this.visibleColumns.indexOf(h.name) != -1
        ),
        //{ text: "Actions", value: "actions", sortable: false, name: "actions" },
      ];
    },
    totalPrice() {
      let total = 0;
      if (this.items.length > 0) {
        total = this.items
          .filter((item) => item.isIncluded)
          .reduce((t, item) => Number(t) + Number(item.price), total);
      }
      return total.toFixed(2);
    },
    filteredHeaders() {
      return this.headers.filter(
        (h) => this.visibleColumns.indexOf(h.name) > -1
      );
    },
  },

  methods: {
    isSellerMpLocked(item) {
      // #11287 part 1: seller's own MP service (source GTWEB) stays "MP Quotation"
      // until the buyer requests it — lock status editing until then.
      return (
        item.source &&
        item.source.name === "GTWEB" &&
        item.status === "MP Quotation"
      );
    },
    sellerMpAllowedStatuses(item) {
      // #11287 part 1b: a seller MP service awaiting confirmation ("Requested by MP")
      // may only be Confirmed or Rejected.
      if (
        item.source &&
        item.source.name === "GTWEB" &&
        item.status === "Requested by MP"
      ) {
        return ["Confirmed", "Rejected"];
      }
      return null;
    },
    onIsIncludedChange(item) {
      this.$emit("isIncludedChange", item);
    },
    onIsOptionalChange(item) {
      this.$emit("isOptionalChange", item);
    },
    onOrderNoChange(item) {
      this.$emit("orderNoChange", item);
    },
    parentIsHidden(extraItem) {
      const parent = this.items.find((item) => item.id == extraItem.itemId);
      const parentIsHidden = parent && parent.isHidden;
      return parentIsHidden;
    },
    onIsHiddenChange(item, $event) {
      if (item.isExtra) {
        const extra = item.extras.find((e) => e.id == item.extraId);
        if (extra) {
          extra.isHidden = $event;
        }
      } else {
        if ($event) {
          //update item extras
          for (const extra of item.extras) {
            extra.isHidden = true;
          }
        }
      }
    },
    onShowExchangeRateUpdater() {
      this.$emit("show-exchange-rate-updater", [...this.selectedRows]);
    },
    onShowItiComps(item) {
      this.$emit("showItiComps", item);
    },
    onEditRemarks(item) {
      this.$emit("showRemarksEdit", item);
    },
    onTimeChange(item) {
      //http://tracker.globetrack.ie/issues/6349
      //console.log("list table onTimeChange", item);
      // if (item.serviceType == "TRANSPORT") {
      //   this.$emit("onRecalculate");
      // }
      this.$emit("timeChange", item);
    },
    onIncludeCommissionChange(item, $event) {
      const extra = item.extras.find((e) => e.id == item.extraId);
      if (extra) {
        extra.includeCommission = $event;
      }
    },
    onItemStatusSelect(item, payload) {
      if (item.isExtra) {
        const editedExtra = item.extras.find((e) => e.id == item.extraId);
        editedExtra.status = payload.name;
        editedExtra.statusId = payload.id;
      } else {
        const editedItem = this.items.find((i) => i.id == item.id);
        editedItem.status = payload.name;
      }
      //add log for "Cancelled with no charge"
      if (payload.id == "U2VydmljZVN0YXR1cy00") {
        item.customPricing = true;
        item.totalPrice = 0;
        this.addLog(item);
      }
    },
    onShowMoveDays() {
      const selected = [...this.selectedRows];
      this.$emit("showMoveDays", selected);
      this.selected = [];
    },
    onReorderServices() {
      const selected = [...this.selectedRows];
      this.$emit("reorderSelectedServices", selected);
      this.selected = [];
    },
    onCustomPriceToggle() {
      const selected = [...this.selectedRows];
      this.$emit("customPriceToggle", selected);
      this.selected = [];
    },
    onZeroPrices() {
      const selected = [...this.selectedRows];
      this.$emit("zeroSelectedServices", selected);
      this.selected = [];
    },
    onPriceAdjuster() {
      const selected = [...this.selectedRows];
      this.$emit("showPriceAdjuster", selected);
      this.selected = [];
    },
    onRefreshPrices() {
      const selected = [...this.selectedRows];
      this.$emit("refreshPrices", selected);
      this.selected = [];
    },
    onShowPaxUpdater() {
      const selected = [...this.selectedRows];
      this.$emit("showPaxUpdater", selected);
      this.selected = [];
    },
    onRemoveSelectedServices() {
      const selected = [...this.selectedRows];
      this.$emit("removeSelectedServices", selected);
      this.selected = [];
    },
    onShowUpdateStatus() {
      const selected = [...this.selectedRows];
      this.$emit("showUpdateStatus", selected);
      this.selected = [];
    },
    showSupplierDetails(item) {
      return () => {
        this.$emit("supplier-edit", item);
      };
    },
    cellContent({ item, header }) {
      let content = item[header.value];
      if (header.name == "status") {
        content = this.statusLabeler(item.status);
      }
      if (header.name == "pax") {
        content = this.paxLabeler(item);
      }
      if (header.name == "price") {
        content = this.priceLabeler(item.price);
      }
      if (header.name == "duration") {
        content = this.duration(item);
      }
      return content;
    },
    priceLabeler(price) {
      return this.$options.filters.priceLabeler(price);
    },
    statusLabeler(status) {
      return status ? status : "Quotation";
    },
    paxLabeler(item) {
      return `${item.adults}/${item.children}/${item.infants}`;
    },
    rowIsDisabled(item) {
      return this.disableRow(item);
    },
    duration(item) {
      let start = moment(item.startDate).startOf("day");
      let end = moment(item.endDate).startOf("day");
      if (start.isValid() && end.isValid()) {
        let d = end.diff(start, "days");
        let addDay =
          item.serviceType != "ACCOMMODATION" || item.isExtra ? 1 : 0;
        //let addDay = 1;
        if (item.serviceType == "TRANSPORT") {
          //http://tracker.globetrack.ie/issues/5905
          // only if both pickup and dropoff times are set
          if (
            item.remarks &&
            item.remarks.pickupTime &&
            item.remarks.pickupTime != "" &&
            item.remarks.dropoffTime &&
            item.remarks.dropoffTime != ""
          ) {
            const pickupTime =
              item.remarks &&
              item.remarks.pickupTime &&
              item.remarks.pickupTime != ""
                ? item.remarks.pickupTime
                : "00:00";
            const dropoffTime =
              item.remarks &&
              item.remarks.dropoffTime &&
              item.remarks.dropoffTime != ""
                ? item.remarks.dropoffTime
                : "00:01";
            addDay =
              Number(dropoffTime.replace(/\D/, "")) >
              Number(pickupTime.replace(/\D/, ""))
                ? 1
                : 0;
          }
        }
        return d == 0 ? 1 : d + addDay;
      }
      return 1;
    },
    customPricingFlagColor(item) {
      return item.customPricing ? "green darken-2" : "grey";
    },
    onItemRemove(item) {
      //console.log("on item remove", item);
      this.$emit("action", { action: "itemRemove", payload: item });
    },
    onEditItemPrices(item) {
      //console.log("on item prices edit", item);
      this.$emit("action", { action: "itemEditPrices", payload: item });
    },
    onItemEdit(item) {
      this.$emit("action", { action: "itemEdit", payload: item });
    },
    onAddItem() {
      this.$emit("action", { action: "addItem", payload: this.group });
    },
    onAddAdhoc() {
      this.$emit("action", { action: "addAdhoc", payload: this.group });
    },

    onExtraRemove(extra, item) {
      //console.log("removing extra: ", extra, "form item: ", item);
      const editedItem = this.items.find((i) => i.id == item.id);
      if (editedItem) {
        editedItem.extras = [
          ...editedItem.extras.filter((e) => e.id != extra.id),
        ];
      }
    },
    onCompsSave(payload) {
      EventBus.$emit("iti-items-refresh");
    },
    parentIsOptional(extraItem) {
      const parent = this.items.find((item) => item.id == extraItem.itemId);
      const parentIsOptional = parent && parent.isOptional;
      return parentIsOptional;
    },
    parentIsIncluded(extraItem) {
      const parent = this.items.find((item) => item.id == extraItem.itemId);
      const parentIsIncluded = parent && parent.isIncluded;
      return parentIsIncluded;
    },
    itemIsDeletable(item) {
      //#11180 — services copied from a departure date are locked on the itinerary
      if (item.fromDeparture) return false;
      if (item.isExtra) {
        const extra = item.extras.find((e) => e.id == item.extraId);
        if (extra && extra.isMandatory) {
          return false;
        } else {
          return true;
        }
      } else {
        return !item.isMandatory;
      }
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/shared/ItineraryItemsListTable.vue